/**
 * Slotmodellen: één centrale vertaling van interne model-id's naar de Antreja-modelnamen.
 *
 * Intern (database-enum `lock_model`, API's, app-domein) blijven de oude leverancierscodes bestaan:
 * `D1` / `D1_R` / `D1_K` (database) en `D1` / `D1-R` / `D1-K` (app). Die wijzigen we bewust niet,
 * omdat een enum-migratie risico geeft. Alles wat een gebruiker ziet loopt via `modelLabel()`.
 *
 *   A1V = vingerafdruk (intern D1)
 *   A1T = tag/pas      (intern D1-R / D1_R)
 *   A1K = klavier      (intern D1-K / D1_K)
 */

/** Database-/API-waarde van het model. */
export type LockModelId = 'D1' | 'D1_R' | 'D1_K';

/** Alle interne waarden in vaste volgorde (A1V, A1T, A1K; volgorde van de database-enum). */
export const LOCK_MODEL_IDS: readonly LockModelId[] = ['D1', 'D1_R', 'D1_K'];

/** Zichtbare Antreja-modelnaam per interne waarde. */
export const MODEL_LABEL: Readonly<Record<LockModelId, string>> = { D1: 'A1V', D1_R: 'A1T', D1_K: 'A1K' };

/** Korte omschrijving van wat het model kan. */
export const MODEL_FEATURES: Readonly<Record<LockModelId, string>> = {
  D1: 'vingerafdruk, pas, app',
  D1_R: 'pas, app · IP65',
  D1_K: 'codeklavier, pas, app',
};

/** Normaliseert elke bekende schrijfwijze (D1-R, d1_r, A1T, …) naar de database-waarde, of `null`. */
export function parseModel(input: string | null | undefined): LockModelId | null {
  if (!input) return null;
  const k = input.trim().toUpperCase().replace(/[\s_-]+/g, '');
  switch (k) {
    case 'A1V':
    case 'D1':
      return 'D1';
    case 'A1T':
    case 'D1R':
      return 'D1_R';
    case 'A1K':
    case 'D1K':
      return 'D1_K';
    default:
      return null;
  }
}

/**
 * Zichtbare modelnaam voor elke interne schrijfwijze (`D1_K`, `D1-K`, …). Onbekende waarden komen
 * ongewijzigd terug, zodat er nooit een lege plek op het scherm staat.
 */
export function modelLabel(model: string | null | undefined): string {
  const id = parseModel(model);
  return id ? MODEL_LABEL[id] : (model ?? '');
}

/** Omschrijving voor elke interne schrijfwijze, of een lege string. */
export function modelFeatures(model: string | null | undefined): string {
  const id = parseModel(model);
  return id ? MODEL_FEATURES[id] : '';
}
